<template>
  <div class="seebeck-pro-viz">
    <div class="viz-header-compact" >
       <div class="brief" v-latex="knowledgePoint"></div>
       <div class="formula-chip" v-latex>$V = S · ΔT$</div>
    </div>

    <div class="split-layout">
      <!-- 仿真区域 -->
      <div class="simulation-stage dark-stage flex-1" style="flex: 1.2;">
        <svg width="100%" height="380" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid meet">
           <defs>
              <filter id="glow-heat"><feGaussianBlur stdDeviation="3" result="blur" /><feComposite in="SourceGraphic" in2="blur" operator="over" /></filter>
              <!-- 渐变：热端颜色 -->
              <radialGradient id="grad-hot" cx="50%" cy="50%" r="50%">
                 <stop offset="0%" stop-color="#ef4444" />
                 <stop offset="100%" stop-color="#7f1d1d" />
              </radialGradient>
              <radialGradient id="grad-cold" cx="50%" cy="50%" r="50%">
                 <stop offset="0%" stop-color="#38bdf8" />
                 <stop offset="100%" stop-color="#1e3a8a" />
              </radialGradient>
           </defs>

           <!-- Grid Background -->
           <g stroke="rgba(241, 245, 249, 0.03)" stroke-width="2">
              <line v-for="i in 17" :key="'v'+i" :x1="i*50" y1="0" :x2="i*50" y2="600" />
              <line v-for="i in 13" :key="'h'+i" x1="0" :y1="i*50" x2="800" :y2="i*50" />
           </g>

           <!-- 不同金属回路 -->
           <g fill="none" stroke-width="8">
              <!-- Metal A (Top - Copper/Crimson) -->
              <path d="M 200,350 L 200,150 L 600,150 L 600,350" stroke="#f43f5e" stroke-linecap="round" />
              <!-- Metal B (Bottom Curve - Constantan/Slate) -->
              <path d="M 200,350 Q 400,450 600,350" stroke="#94a3b8" stroke-linecap="round" />
           </g>

           <!-- 接点 Junctions -->
           <!-- Junction 1: Hot -->
           <g transform="translate(200, 350)">
              <circle r="25" :fill="hotFill" filter="url(#glow-heat)" />
              <text y="50" fill="#f87171" font-size="20" font-weight="900" text-anchor="middle">热端 (TH)</text>
              <text y="75" fill="#fca5a5" font-size="16" text-anchor="middle">{{ tempHot }}°C</text>
           </g>

           <!-- Junction 2: Cold -->
           <g transform="translate(600, 350)">
              <circle r="25" fill="url(#grad-cold)" />
              <text y="50" fill="#38bdf8" font-size="20" font-weight="900" text-anchor="middle">冷端 (TC)</text>
              <text y="75" fill="#7dd3fc" font-size="16" text-anchor="middle">{{ tempCold }}°C</text>
           </g>

           <!-- 电压表 Voltmeter HUD -->
           <g transform="translate(400, 150)">
              <rect x="-60" y="-80" width="120" height="60" fill="#0f172a" stroke="#10b981" stroke-width="3" rx="8" />
              <text y="-40" fill="#10b981" font-size="22" font-weight="900" text-anchor="middle">{{ voltage.toFixed(2) }} mV</text>
           </g>

           <!-- 电子流动动画 (基于温差大小控制速度) -->
           <g v-if="voltage > 0.1">
              <circle v-for="n in 8" :key="n" r="3" fill="#fff" filter="url(#glow-heat)">
                 <animateMotion 
                    :path="`M 200,350 L 200,150 L 600,150 L 600,350 Q 400,450 200,350`" 
                    :dur="`${Math.max(0.5, 15/(voltage + 0.1))}s`" 
                    repeatCount="indefinite" 
                    :begin="`${n * 0.5}s`" 
                 />
              </circle>
           </g>

           <!-- 能量转化指示 -->
           <g v-if="voltage > 5" transform="translate(400, 520)">
              <text fill="#facc15" font-size="24" font-weight="900" text-anchor="middle" filter="url(#glow-heat)">热能 ➔ 电能 (Seebeck)</text>
           </g>
        </svg>
      </div>

      <!-- 右侧解析面板 -->
      <div class="simulation-stage logic-panel">
         <div class="logic-header">热电物理过程解析</div>
         <div class="flex-column-center" style="flex:1; padding: 15px;">
            <div class="eq-box text-center">
               <div class="eq-display text-blue">实时物理数据</div>
               <div class="seq-data-list">
                  <div class="data-row"><span>热端温度 (TH)</span><span class="hl text-red">{{ tempHot }} °C</span></div>
                  <div class="data-row"><span>冷端温度 (TC)</span><span class="hl text-blue">{{ tempCold }} °C</span></div>
                  <div class="data-row" style="background: rgba(244,63,94,0.1)">
                     <span>系统有效温差 (ΔT)</span><span class="hl text-red">{{ tempHot - tempCold }} K</span>
                  </div>
                  <div class="data-row"><span>塞贝克系数 (S_AB)</span><span class="hl">约 40 μV/K</span></div>
                  <div class="divider"></div>
                  <div class="data-row" style="background: rgba(16,185,129,0.1)">
                     <span>回路热电势 (V)</span><span class="hl text-green">{{ voltage.toFixed(3) }} mV</span>
                  </div>
               </div>
            </div>

            <div class="eq-box mt-l">
               <p class="desc-text" style="font-size: 13px;"><strong>核心洞察：</strong><br/><br/>{{ tipDesc }}</p>
            </div>
         </div>
      </div>
    </div>

    <!-- 控制台 -->
    <div class="compact-controls">
      <div class="control-card">
         <div class="control-grid-mini">
            <div class="s-item" style="grid-column: span 1;">
               <label>调节热端热源温度 (TH)</label>
               <el-slider v-model="tempHot" :min="20" :max="500" size="small"></el-slider>
            </div>
            
            <div class="s-item" style="grid-column: span 1;">
               <label>环境维持冷端温度 (TC)</label>
               <el-slider v-model="tempCold" :min="0" :max="100" size="small"></el-slider>
            </div>

            <div class="s-item" style="grid-column: span 2;">
               <p style="font-size: 11px; color: #94a3b8; margin: 0;">* 模拟 Copper-Constantan (T型) 热电偶对在标准温差下的电势输出</p>
            </div>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SeebeckEffectViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      tempHot: 100,
      tempCold: 20
    }
  },
  computed: {
    topicIntro() {
       return '塞贝克效应：当闭合回路中两种不同导体接点存在温度差时，回路中将产生电势流。';
    },
    hotFill() {
       const ratio = Math.min(1, Math.max(0, (this.tempHot - 20) / 480));
       if (ratio < 0.2) return 'url(#grad-cold)';
       return 'url(#grad-hot)';
    },
    voltage() {
       // S approx 40 uV/K = 0.04 mV/K
       const deltaT = Math.max(0, this.tempHot - this.tempCold);
       return deltaT * 0.04;
    },
    tipDesc() {
       return '塞贝克效应展示了**热能与电能的直接转换**。在微观层面，由于热端电子动能更高，它们会自发向冷端扩散，直到建立起反向电场达到平衡，这种电位的积累就是温差电势。它是【热电偶温度计】的核心原理：只要测出毫伏电压，就能反推温度。';
    }
  }
}
</script>

<style scoped lang="scss">
.seebeck-pro-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow-y: auto; overflow-x: hidden; padding-right: 5px; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 15px; color: #1e293b; font-weight: 700; } .formula-chip { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; padding: 8px 18px; border-radius: 6px; font-weight: 900; font-family: -apple-system, sans-serif; font-size: 18px; } }
.split-layout { display: flex; gap: 12px; flex: 1; min-height: 360px; }
.flex-1 { flex: 1; min-width: 0; }
.logic-panel { flex: 0.8; min-width: 0; display: flex; flex-direction: column; background: #0f172a !important; border-radius: 12px; }
.logic-header { background: #1e293b; padding: 12px; color: #38bdf8; font-size: 14px; font-weight: 900; text-align: center; border-bottom: 2px solid #0f172a; border-radius: 12px 12px 0 0; }
.flex-column-center { display: flex; flex-direction: column; justify-content: flex-start; overflow-y: auto; }

.eq-box { width: 100%; border-radius: 8px; overflow: hidden; }
.eq-display { background: rgba(0,0,0,0.4); padding: 10px; font-weight: 900; border-bottom: 1px solid rgba(255,255,255,0.05); }
.seq-data-list { display: flex; flex-direction: column; background: rgba(15, 23, 42, 0.6); }
.data-row { display: flex; justify-content: space-between; align-items: center; padding: 12px 18px; border-bottom: 1px solid rgba(255,255,255,0.02); color: #94a3b8; font-size: 13px; font-weight: bold; }
.hl { color: #f8fafc; font-weight: 900; font-family: monospace; font-size: 14px; }
.divider { height: 1px; background: rgba(255,255,255,0.1); margin: 4px 0; }

.text-yellow { color: #facc15; } .text-green { color: #10b981; } .text-blue { color: #38bdf8; } .text-red { color: #f43f5e; }
.desc-text { color: #cbd5e1; font-size: 13px; line-height: 1.6; padding: 16px; background: rgba(56, 189, 248, 0.05); border-radius: 8px; border: 1px solid rgba(56, 189, 248, 0.1); }
.mt-l { margin-top: 25px; }

.simulation-stage { background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }

.compact-controls { flex: 0; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: #ffffff; padding: 16px 20px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; .s-item { label { font-size: 13px; color: #475569; font-weight: 900; margin-bottom: 8px; display: block; } } }
</style>
